import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { Link, useParams } from "react-router-dom";
import {
  listLeagues,
  listOrganizations,
  listPitchLogs,
  listPlayerParents,
  listRestProfiles,
  listRestRules,
  listRosterMemberships,
  listTeamPlayers,
  listTeams,
  listUserAssignments
} from "../graphql/queries";
import { listPlayersWithTraits } from "../graphql/playerTraitQueries";
import { createPlayerParent, deletePlayerParent, updatePlayer, updatePlayerParent } from "../graphql/mutations";
import { evaluatePitcherAvailability } from "../lib/rulesEngine";
import { formatApiError } from "../lib/formatApiError";
import { buildCoachTeamIds, buildPlayerTeamIds, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import PlayerTraitBadges from "../components/PlayerTraitBadges";

interface PlayerDetailPageProps {
  userRoles: string[];
  playerIdOverride?: string;
  embedded?: boolean;
}

interface Player {
  id: string;
  name: string;
  teamId: string;
  dateOfBirth?: string | null;
  town?: string | null;
  state?: string | null;
  pitcher?: boolean | null;
  catcher?: boolean | null;
  battingHandedness?: string | null;
  throwingHandedness?: string | null;
}

interface Team {
  id: string;
  name: string;
  coachUsernames?: string[] | null;
}

interface TeamPlayer {
  id: string;
  teamID: string;
  playerID: string;
}

interface RosterMembership extends RosterMembershipLike {}

interface UserAssignment extends UserAssignmentLike {}

interface PlayerParent {
  id: string;
  playerId: string;
  parentEmail: string;
}

interface PitchLog {
  id: string;
  playerId: string;
  teamId: string;
  date: string;
  pitches: number;
  outsRecorded?: number | null;
  isSubstitute?: boolean | null;
  enteredByUsername?: string | null;
  lastModifiedByUsername?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const formatInningsFromOuts = (outs?: number | null): string => {
  if (typeof outs !== "number" || outs < 0) {
    return "-";
  }
  const whole = Math.floor(outs / 3);
  const remainder = outs % 3;
  return `${whole}.${remainder}`;
};

const PlayerDetailPage: React.FC<PlayerDetailPageProps> = ({ userRoles, playerIdOverride, embedded = false }) => {
  const params = useParams();
  const playerId = playerIdOverride ?? params.playerId;

  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [playerParents, setPlayerParents] = useState<PlayerParent[]>([]);
  const [logs, setLogs] = useState<PitchLog[]>([]);
  const [rules, setRules] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [leagues, setLeagues] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);

  const [editName, setEditName] = useState("");
  const [editDob, setEditDob] = useState("");
  const [editTown, setEditTown] = useState("");
  const [editState, setEditState] = useState("");
  const [editParentEmail, setEditParentEmail] = useState("");
  const [editPitcher, setEditPitcher] = useState(false);
  const [editCatcher, setEditCatcher] = useState(false);
  const [editBattingHandedness, setEditBattingHandedness] = useState("");
  const [editThrowingHandedness, setEditThrowingHandedness] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const canManage =
    userRoles.includes("SuperAdmin") ||
    userRoles.includes("Commish") ||
    userRoles.includes("Coach");
  const isPrivileged = userRoles.includes("Commish") || userRoles.includes("SuperAdmin");
  const hasCoachRole = userRoles.includes("Coach");
  const hasParentRole = userRoles.includes("Parent") || userRoles.includes("Parents");

  const teamById = useMemo(() => {
    const out: Record<string, Team> = {};
    for (const team of teams) {
      out[team.id] = team;
    }
    return out;
  }, [teams]);

  const playerById = useMemo(() => {
    const out: Record<string, Player> = {};
    for (const player of players) {
      out[player.id] = player;
    }
    return out;
  }, [players]);

  const parentByPlayerId = useMemo(() => {
    const out: Record<string, PlayerParent> = {};
    for (const parent of playerParents) {
      if (!out[parent.playerId]) {
        out[parent.playerId] = parent;
      }
    }
    return out;
  }, [playerParents]);

  const teamIdsByPlayerId = useMemo(
    () => buildPlayerTeamIds({ players, teamPlayers, rosterMemberships }),
    [players, rosterMemberships, teamPlayers]
  );

  const coachTeamIds = useMemo(() => {
    if (!hasCoachRole || isPrivileged || !username) {
      return new Set<string>();
    }
    return buildCoachTeamIds({ username, teams, userAssignments });
  }, [hasCoachRole, isPrivileged, teams, userAssignments, username]);

  const parentScopedPlayerIds = useMemo(() => {
    if (!hasParentRole || isPrivileged || !userEmail.trim()) {
      return new Set<string>();
    }
    const normalized = userEmail.trim().toLowerCase();
    return new Set(
      playerParents
        .filter((link) => (link.parentEmail ?? "").trim().toLowerCase() === normalized)
        .map((link) => link.playerId)
    );
  }, [hasParentRole, isPrivileged, playerParents, userEmail]);

  const visiblePlayerIds = useMemo(() => {
    if (isPrivileged || (!hasCoachRole && !hasParentRole)) {
      return new Set(players.map((player) => player.id));
    }
    const out = new Set<string>();
    if (hasCoachRole) {
      for (const player of players) {
        const memberships = teamIdsByPlayerId[player.id] ?? new Set<string>();
        if ([...memberships].some((teamId) => coachTeamIds.has(teamId))) {
          out.add(player.id);
        }
      }
    }
    for (const id of parentScopedPlayerIds) {
      out.add(id);
    }
    return out;
  }, [coachTeamIds, hasCoachRole, hasParentRole, isPrivileged, parentScopedPlayerIds, players, teamIdsByPlayerId]);

  const selectedPlayer = playerId ? playerById[playerId] : undefined;
  const canViewPlayer = Boolean(selectedPlayer && visiblePlayerIds.has(selectedPlayer.id));
  const canEditPlayer = canManage && canViewPlayer;

  const selectedPlayerLogs = useMemo(() => {
    if (!selectedPlayer) {
      return [] as PitchLog[];
    }
    return logs
      .filter((log) => log.playerId === selectedPlayer.id)
      .sort((a, b) => {
        if (a.date === b.date) {
          return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
        }
        return b.date.localeCompare(a.date);
      });
  }, [logs, selectedPlayer]);

  const logTotals = useMemo(() => {
    const totalPitches = selectedPlayerLogs.reduce((sum, log) => sum + (log.pitches ?? 0), 0);
    const totalOuts = selectedPlayerLogs.reduce((sum, log) => sum + (typeof log.outsRecorded === "number" ? log.outsRecorded : 0), 0);
    return {
      totalPitches,
      totalOuts,
      totalInnings: formatInningsFromOuts(totalOuts)
    };
  }, [selectedPlayerLogs]);

  const selectedAvailability = useMemo(() => {
    if (!selectedPlayer) {
      return null;
    }
    const computed = evaluatePitcherAvailability({
      players: [selectedPlayer],
      teams,
      teamPlayers,
      leagues,
      organizations,
      rules,
      profiles,
      logs: selectedPlayerLogs
    });
    return computed[0] ?? null;
  }, [leagues, organizations, profiles, rules, selectedPlayer, selectedPlayerLogs, teamPlayers, teams]);

  const violationsByLogId = useMemo(() => {
    const out: Record<string, Array<{ type: string; ruleName: string; source: string; message: string }>> = {};
    for (const violation of selectedAvailability?.violations ?? []) {
      const key = violation.logId ?? violation.occurredOn;
      if (!out[key]) {
        out[key] = [];
      }
      out[key].push({
        type: violation.type,
        ruleName: violation.ruleName,
        source: violation.source,
        message: violation.message
      });
    }
    return out;
  }, [selectedAvailability]);

  const loadData = async () => {
    try {
      setErrorMessage("");
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      setUserEmail((authUser?.attributes?.email ?? "").toLowerCase());

      const [playersRes, teamsRes, teamPlayersRes, rosterMembershipsRes, userAssignmentsRes, parentsRes, logsRes, leaguesRes, orgsRes, rulesRes, profilesRes] = await Promise.all([
        API.graphql(graphqlOperation(listPlayersWithTraits, { limit: 2000 })),
        API.graphql(graphqlOperation(listTeams, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 4000 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 })),
        API.graphql(graphqlOperation(listPlayerParents, { limit: 4000 })),
        API.graphql(graphqlOperation(listPitchLogs, { limit: 6000 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 1000 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 1000 })),
        API.graphql(graphqlOperation(listRestRules, { limit: 1000 })),
        API.graphql(graphqlOperation(listRestProfiles, { limit: 3000 }))
      ]);

      const loadedPlayers = ((playersRes as any)?.data?.listPlayers?.items ?? []).filter(Boolean);
      const loadedTeams = ((teamsRes as any)?.data?.listTeams?.items ?? []).filter(Boolean);
      const loadedTeamPlayers = ((teamPlayersRes as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean);
      const loadedRosterMemberships = ((rosterMembershipsRes as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean);
      const loadedUserAssignments = ((userAssignmentsRes as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean);
      const loadedParents = ((parentsRes as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean);
      const loadedLogs = ((logsRes as any)?.data?.listPitchLogs?.items ?? []).filter(Boolean);
      const loadedLeagues = ((leaguesRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean);
      const loadedOrgs = ((orgsRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean);
      const loadedRules = ((rulesRes as any)?.data?.listRestRules?.items ?? []).filter(Boolean);
      const loadedProfiles = ((profilesRes as any)?.data?.listRestProfiles?.items ?? []).filter(Boolean);

      setPlayers(loadedPlayers);
      setTeams(loadedTeams);
      setTeamPlayers(loadedTeamPlayers);
      setRosterMemberships(loadedRosterMemberships);
      setUserAssignments(loadedUserAssignments);
      setPlayerParents(loadedParents);
      setLogs(loadedLogs);
      setLeagues(loadedLeagues);
      setOrganizations(loadedOrgs);
      setRules(loadedRules);
      setProfiles(loadedProfiles);
    } catch (error) {
      setErrorMessage(`Failed to load player details: ${formatApiError(error)}`);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedPlayer) {
      return;
    }
    setEditName(selectedPlayer.name ?? "");
    setEditDob(selectedPlayer.dateOfBirth ?? "");
    setEditTown(selectedPlayer.town ?? "");
    setEditState(selectedPlayer.state ?? "");
    setEditParentEmail(parentByPlayerId[selectedPlayer.id]?.parentEmail ?? "");
    setEditPitcher(Boolean(selectedPlayer.pitcher));
    setEditCatcher(Boolean(selectedPlayer.catcher));
    setEditBattingHandedness((selectedPlayer.battingHandedness ?? "").toUpperCase());
    setEditThrowingHandedness((selectedPlayer.throwingHandedness ?? "").toUpperCase());
  }, [parentByPlayerId, selectedPlayer]);

  const handleSave = async () => {
    if (!selectedPlayer || !canEditPlayer) {
      return;
    }
    if (!editName.trim()) {
      setErrorMessage("Player name is required.");
      return;
    }
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await API.graphql(
        graphqlOperation(updatePlayer, {
          input: {
            id: selectedPlayer.id,
            name: editName.trim(),
            dateOfBirth: editDob || null,
            town: editTown.trim() || null,
            state: editState.trim().toUpperCase() || null,
            pitcher: editPitcher,
            catcher: editCatcher,
            battingHandedness: editBattingHandedness || null,
            throwingHandedness: editThrowingHandedness || null
          }
        })
      );

      const existingParent = parentByPlayerId[selectedPlayer.id];
      if (editParentEmail.trim()) {
        if (existingParent) {
          await API.graphql(
            graphqlOperation(updatePlayerParent, {
              input: { id: existingParent.id, parentEmail: editParentEmail.trim().toLowerCase() }
            })
          );
        } else {
          await API.graphql(
            graphqlOperation(createPlayerParent, {
              input: { playerId: selectedPlayer.id, parentEmail: editParentEmail.trim().toLowerCase() }
            })
          );
        }
      } else if (existingParent) {
        await API.graphql(graphqlOperation(deletePlayerParent, { input: { id: existingParent.id } }));
      }

      setSuccessMessage("Player updated.");
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to save player: ${formatApiError(error)}`);
    } finally {
      setSaving(false);
    }
  };

  if (!playerId) {
    return (
      <div className="max-w-5xl mx-auto">
        <p className="text-gray-600">No player selected.</p>
      </div>
    );
  }

  if (!selectedPlayer) {
    return (
      <div className="max-w-5xl mx-auto">
        <p className="text-gray-600 mb-4">Player not found.</p>
        <Link to="/admin/players" className="table-sort">Back to Players</Link>
      </div>
    );
  }

  if (!canViewPlayer) {
    return (
      <div className="max-w-5xl mx-auto">
        <p className="text-gray-600 mb-4">You do not have access to this player.</p>
        <Link to="/admin/players" className="table-sort">Back to Players</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {!embedded && (
        <div className="mb-4">
          <Link to="/admin/players" className="table-sort">Back to Players</Link>
        </div>
      )}
      <h1 className="text-2xl font-bold mb-2">{selectedPlayer.name}</h1>
      <div className="mb-3">
        <PlayerTraitBadges player={selectedPlayer} />
      </div>
      <p className="text-gray-600 mb-6">Player detail, edit profile, logs, and calculated violations.</p>

      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}
      {successMessage && <p className="mb-4" style={{ color: "#166534" }}>{successMessage}</p>}

      <div className="bubble-card p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Player Info</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input value={editName} onChange={(e) => setEditName(e.target.value)} className="border px-3 py-2 rounded" disabled={!canEditPlayer} />
          <input type="date" value={editDob} onChange={(e) => setEditDob(e.target.value)} className="border px-3 py-2 rounded" disabled={!canEditPlayer} />
          <input value={editParentEmail} onChange={(e) => setEditParentEmail(e.target.value)} placeholder="Parent email" className="border px-3 py-2 rounded" disabled={!canEditPlayer} />
          <input value={editTown} onChange={(e) => setEditTown(e.target.value)} placeholder="Town" className="border px-3 py-2 rounded" disabled={!canEditPlayer} />
          <input value={editState} onChange={(e) => setEditState(e.target.value)} placeholder="State" className="border px-3 py-2 rounded" disabled={!canEditPlayer} />
          <input value={teamById[selectedPlayer.teamId]?.name ?? "Unknown team"} className="border px-3 py-2 rounded bg-gray-50" disabled />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={editPitcher} onChange={(e) => setEditPitcher(e.target.checked)} disabled={!canEditPlayer} />
            Can pitch
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={editCatcher} onChange={(e) => setEditCatcher(e.target.checked)} disabled={!canEditPlayer} />
            Can catch
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            <span>Batting Hand</span>
            <select value={editBattingHandedness} onChange={(e) => setEditBattingHandedness(e.target.value)} className="border px-3 py-2 rounded" disabled={!canEditPlayer}>
              <option value="">Unknown</option>
              <option value="L">L</option>
              <option value="R">R</option>
              <option value="B">B</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            <span>Throwing Hand</span>
            <select value={editThrowingHandedness} onChange={(e) => setEditThrowingHandedness(e.target.value)} className="border px-3 py-2 rounded" disabled={!canEditPlayer}>
              <option value="">Unknown</option>
              <option value="L">L</option>
              <option value="R">R</option>
            </select>
          </label>
        </div>
        {canEditPlayer && (
          <div className="mt-3">
            <button onClick={handleSave} className="bg-blue-600 text-white px-4 py-2 rounded" disabled={saving}>
              {saving ? "Saving..." : "Save Player"}
            </button>
          </div>
        )}
      </div>

      <div className="bubble-card p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">Pitch Logs</h2>
        <div className="bubble-table-wrapper">
          <table className="bubble-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Team</th>
                <th>Pitches</th>
                <th>Outs</th>
                <th>Innings</th>
                <th>Substitute</th>
                <th>Status</th>
                <th>Last Modified By</th>
              </tr>
            </thead>
            <tbody>
              {selectedPlayerLogs.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-gray-600">No pitch logs found.</td>
                </tr>
              )}
              {selectedPlayerLogs.map((log) => {
                const logViolations = violationsByLogId[log.id] ?? violationsByLogId[log.date] ?? [];
                const hasViolation = logViolations.length > 0;
                const violationLabels = Array.from(
                  new Set(
                    logViolations.map((item) => {
                      if (item.type === "PITCH_LIMIT") {
                        return "Pitch Limit";
                      }
                      if (item.type === "REST_INTERVAL") {
                        return "Short Rest";
                      }
                      if (item.type === "WEEKLY_OUTS") {
                        return "Out Limit";
                      }
                      return "Violation";
                    })
                  )
                );
                return (
                  <tr
                    key={log.id}
                    style={hasViolation ? { backgroundColor: "#fff1f2", borderLeft: "6px solid #be123c" } : undefined}
                  >
                    <td>{log.date}</td>
                    <td>{teamById[log.teamId]?.name ?? log.teamId}</td>
                    <td>{log.pitches}</td>
                    <td>{typeof log.outsRecorded === "number" ? log.outsRecorded : "-"}</td>
                    <td>{formatInningsFromOuts(log.outsRecorded)}</td>
                    <td>{log.isSubstitute ? "Yes" : "No"}</td>
                    <td>
                      {hasViolation ? (
                        <div>
                          <div className="bubble-pill-wrap">
                            {violationLabels.map((label) => (
                              <span key={`${log.id}-${label}`} className="bubble-pill" style={{ background: "#be123c", color: "#fff", borderColor: "#be123c" }}>
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-600">OK</span>
                      )}
                    </td>
                    <td>{log.lastModifiedByUsername ?? log.enteredByUsername ?? "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-sm text-gray-700">
          <span className="font-medium">Totals:</span>{" "}
          {logTotals.totalPitches} pitches, {logTotals.totalOuts} outs, {logTotals.totalInnings} innings
        </div>
      </div>

      <div className="bubble-card p-4">
        <h2 className="text-lg font-semibold mb-3">Violations</h2>
        <div className="bubble-table-wrapper">
          <table className="bubble-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Rule</th>
                <th>Source</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {!selectedAvailability || selectedAvailability.violations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-gray-600">No violations found.</td>
                </tr>
              ) : (
                selectedAvailability.violations.map((violation) => (
                  <tr key={violation.id}>
                    <td>{violation.occurredOn}</td>
                    <td>{violation.type}</td>
                    <td>{violation.ruleName}</td>
                    <td>{violation.source}</td>
                    <td>{violation.message}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetailPage;
